/**
 * Couple flow shared sub-components.
 *
 * CPBackdrop — full-bleed rose/quiet gradient backdrop.
 * CPStars    — deterministic star field SVG.
 * CPAvatar   — re-exported CCAvatar (same gradient tiles).
 *
 * All hex values inside gradient strings match CSS token values.
 * Strict TypeScript, no `any`.
 */

import styles from './Couple.module.css';

// --------------------------------------------------------------------------
// CPAvatar — re-export CCAvatar to keep API surface consistent
// --------------------------------------------------------------------------

export { CCAvatar as CPAvatar } from '../Compat/shared';
export type { CCavatarProps as CPAvatarProps } from '../Compat/shared';

// --------------------------------------------------------------------------
// CPBackdrop
// --------------------------------------------------------------------------

export type CPBackdropTone = 'rose' | 'quiet';

export interface CPBackdropProps {
  tone?: CPBackdropTone;
}

export function CPBackdrop({ tone = 'quiet' }: CPBackdropProps): JSX.Element {
  // Token-aligned gradient presets:
  //   --rose = #ff8eb0, --rose-deep = #c84d7a
  //   --void = #05030f, --midnight = #0d0824
  //   --amber = #e89a3c
  const presets: Record<CPBackdropTone, string> = {
    rose: [
      'radial-gradient(ellipse 480px 360px at 50% 18%, rgba(255,142,176,0.28), transparent 65%)',
      'radial-gradient(ellipse 360px 280px at 80% 8%,  rgba(232,154,60,0.18),   transparent 65%)',
      'radial-gradient(ellipse 420px 320px at 50% 95%, rgba(200,77,122,0.15),   transparent 70%)',
      'linear-gradient(180deg, #05030f 0%, #0d0824 60%, #05030f 100%)',
    ].join(','),
    quiet: [
      'radial-gradient(ellipse 380px 280px at 70% 0%, rgba(255,142,176,0.14), transparent 65%)',
      'linear-gradient(180deg, #0d0824 0%, #05030f 100%)',
    ].join(','),
  };

  return (
    <div
      className={styles.cpBackdrop}
      style={{ background: presets[tone] }}
      aria-hidden="true"
    />
  );
}

// --------------------------------------------------------------------------
// CPStars
// --------------------------------------------------------------------------

export interface CPStarsProps {
  count?: number;
  seed?: number;
}

export function CPStars({ count = 50, seed = 0 }: CPStarsProps): JSX.Element {
  function pseudo(n: number): number {
    const x = Math.sin((n + seed) * 9999) * 10000;
    return x - Math.floor(x);
  }

  return (
    <svg
      className={styles.cpStars}
      width="100%"
      height="100%"
      aria-hidden="true"
    >
      {Array.from({ length: count }).map((_, i) => (
        <circle
          key={i}
          cx={pseudo(i * 1.7) * 390}
          cy={pseudo(i * 3.3) * 880}
          r={pseudo(i * 5.1) * 1.3 + 0.3}
          fill="#fff8d8"
          opacity={pseudo(i * 7.7) * 0.7 + 0.2}
        />
      ))}
    </svg>
  );
}
